<template>
  <div class="blissrose-training">
    <el-card shadow="never" class="table-card">
      <div slot="header" class="table-header">
        <span>训练任务</span>
        <div class="table-header-right">
          <el-input
            v-model="keyword"
            class="keyword-input"
            size="small"
            placeholder="按任务名 / 数据集 / 派发人筛选"
            prefix-icon="el-icon-search"
            clearable
          />
          <el-select v-model="stateFilter" class="state-select" size="small" clearable placeholder="全部状态">
            <el-option
              v-for="(meta, key) in TRAIN_STATES"
              :key="key"
              :label="meta.label"
              :value="key"
            />
          </el-select>
          <span class="total-count">共 {{ filteredRuns.length }} 个</span>
          <el-button size="small" icon="el-icon-refresh" @click="loadRuns">刷新</el-button>
          <el-button
            type="danger"
            size="small"
            icon="el-icon-delete"
            :disabled="!selectedCount"
            :loading="batchDeleting"
            @click="handleBatchDelete"
          >{{ batchLabel }}</el-button>
          <el-button type="primary" size="small" icon="el-icon-video-play" @click="dispatchVisible = true">
            派发训练
          </el-button>
        </div>
      </div>

      <el-table
        v-loading="loading"
        :data="filteredRuns"
        stripe
        border
        size="small"
        style="width:100%"
        @selection-change="handleSelectionChange"
      >
        <el-table-column type="selection" width="46" :selectable="canDelete" />
        <el-table-column label="任务名" min-width="180" show-overflow-tooltip>
          <template slot-scope="{ row }">
            <span class="mono">{{ row.run_name }}</span>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="110">
          <template slot-scope="{ row }">
            <el-tag :type="stateMeta(row).tag" size="mini">{{ stateMeta(row).label }}</el-tag>
            <div v-if="isStopping(row)" class="cell-sub">
              {{ row.stopped_by }} 已请求停止
            </div>
            <div v-else-if="row.exit_code" class="cell-sub">
              退出码 {{ row.exit_code }}
            </div>
          </template>
        </el-table-column>
        <el-table-column label="进度" width="180">
          <template slot-scope="{ row }">
            <el-progress
              :percentage="percentOf(row)"
              :status="progressStatus(row)"
              :stroke-width="10"
              :show-text="false"
            />
            <div class="cell-sub">
              <span v-if="row.progress.epochs_total">
                {{ row.progress.epochs_done }} / {{ row.progress.epochs_total }} 轮
              </span>
              <span v-else>还没有轮次</span>
              <span :class="{ stale: isStale(row) }"> · 产物更新 {{ relativeTime(row.last_modified) }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="数据集 / 参数" min-width="170" show-overflow-tooltip>
          <template slot-scope="{ row }">
            <span class="mono">{{ dash(row.out_name) }}</span>
            <!-- 训练参数派发后不可改 -->
            <div v-if="row.epochs" class="cell-sub">
              {{ row.epochs }} 轮 · batch {{ row.batch }} · imgsz {{ row.imgsz }}
            </div>
          </template>
        </el-table-column>
        <el-table-column label="显卡" width="70">
          <template slot-scope="{ row }">
            <span v-if="(row.gpu_ids || []).length" class="mono">{{ row.gpu_ids.join(', ') }}</span>
            <span v-else class="muted">-</span>
          </template>
        </el-table-column>
        <el-table-column label="产物" width="88">
          <template slot-scope="{ row }">
            <el-tag :type="row.artifacts.complete ? 'success' : 'info'" size="mini">
              {{ row.artifacts.complete ? '权重齐全' : `缺 ${row.artifacts.missing.length} 个文件` }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="归档" width="82">
          <template slot-scope="{ row }">
            <!-- 训练中一律灰杠：答的是「推成功没有」 -->
            <span v-if="row.state === 'running'" class="muted" title="训练结束后自动归档">-</span>
            <el-tooltip
              v-else
              :content="pushTooltip(row) || '还没有归档'"
              :disabled="!pushTooltip(row)"
              placement="top"
            >
              <el-tag :type="pushMeta(row.push_state).tag" size="mini">
                {{ pushMeta(row.push_state).label }}
              </el-tag>
            </el-tooltip>
          </template>
        </el-table-column>
        <el-table-column prop="dispatched_by" label="派发人" width="82" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ dash(row.dispatched_by) }}</template>
        </el-table-column>
        <el-table-column label="派发时间" width="150">
          <template slot-scope="{ row }">{{ formatTime(row.dispatched_at) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="205" align="center">
          <template slot-scope="{ row }">
            <el-button type="text" size="mini" @click="handleDetail(row)">详情</el-button>
            <el-button
              v-if="row.state === 'running'"
              type="text"
              size="mini"
              class="danger-text"
              :loading="stoppingName === row.run_name"
              @click="handleStop(row)"
            >停止</el-button>
            <!-- 判据由训练机给，这里只读结论 -->
            <el-button
              v-if="row.archivable"
              type="text"
              size="mini"
              :loading="pushingName === row.run_name"
              @click="handlePush(row)"
            >归档产物</el-button>
            <!-- 登记为模型：产物上了 MinIO 才有得导，点开是预填好的导入框，确认才登记 -->
            <span v-if="row.push_state === 'pushed' && row.model_id" class="muted" :title="`已登记为模型 ${row.model_id}`">已登记</span>
            <el-button
              v-else-if="row.push_state === 'pushed'"
              type="text"
              size="mini"
              @click="handleRegister(row)"
            >登记为模型</el-button>
            <!-- 在跑的不给删，与「停止」互斥 -->
            <el-button
              v-if="row.state !== 'running'"
              type="text"
              size="mini"
              class="danger-text"
              :loading="deletingName === row.run_name"
              @click="handleDelete(row)"
            >删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <training-dispatch-dialog :visible.sync="dispatchVisible" @dispatched="loadRuns" />
    <training-detail-drawer
      :visible.sync="detailVisible"
      :run="detailRun"
      @updated="handleRowUpdated"
    />
    <model-import-dialog
      :visible.sync="importVisible"
      :preset-run-name="importRunName"
      @imported="handleModelImported"
    />
  </div>
</template>

<script>
import dayjs from 'dayjs';
import TrainingDispatchDialog from '../../components/TrainingDispatchDialog.vue';
import TrainingDetailDrawer from '../../components/TrainingDetailDrawer.vue';
import ModelImportDialog from '../../components/ModelImportDialog.vue';
import { deleteRun, fetchRuns, pushRun, stopRun } from '../../api/blissrose/training';
import { ARCHIVE_STATES, TRAIN_STATES, TRAIN_STOPPING } from '../../utils/labels';

// 任务状态由训练机现算、平台不落库；静默刷新不打断操作
const POLL_INTERVAL_MS = 10000;
// 训练中且产物超过这么久没更新，把「产物更新」标黄
const STALE_MINUTES = 30;

export default {
  name: 'BlissroseTraining',

  components: { TrainingDispatchDialog, TrainingDetailDrawer, ModelImportDialog },

  data() {
    return {
      TRAIN_STATES,
      loading: false,
      runs: [],
      keyword: '',
      stateFilter: '',
      dispatchVisible: false,
      detailVisible: false,
      detailRun: null,
      importVisible: false,
      importRunName: '',
      stoppingName: '',
      pushingName: '',
      deletingName: '',
      batchDeleting: false,
      selected: [],
      pollTimer: null,
    };
  },

  computed: {
    selectedCount() {
      return this.selected.length;
    },

    batchLabel() {
      return this.selectedCount ? `删除选中（${this.selectedCount}）` : '删除选中';
    },

    /** 筛选在本地做，不跑后端 */
    filteredRuns() {
      const kw = this.keyword.trim().toLowerCase();
      return this.runs.filter(row => {
        if (this.stateFilter && row.state !== this.stateFilter) return false;
        if (!kw) return true;
        // 权重两个字段都收：老行只有 ID，新行有名字
        const fields = [row.run_name, row.out_name, row.dispatched_by, row.model_name, row.model_id];
        return fields.filter(Boolean).some(v => String(v).toLowerCase().includes(kw));
      });
    },
  },

  async mounted() {
    await this.loadRuns();
    // 静默轮询把容器与磁盘变化带进页面；有勾选时停一轮，避免换数据打断勾选
    this.pollTimer = setInterval(() => {
      if (!this.loading && !this.selected.length) this.loadRuns(true);
    }, POLL_INTERVAL_MS);
  },

  beforeDestroy() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  },

  methods: {
    /** 拉取任务列表；silent 为定时轮询，不显示加载态 */
    async loadRuns(silent = false) {
      if (!silent) this.loading = true;
      try {
        const { items } = (await fetchRuns()).data;
        this.runs = items || [];
      } catch (err) {
        // 错误提示由响应拦截器统一弹出，保留上一次的列表
      } finally {
        this.loading = false;
      }
    },

    // ====== 展示辅助 ======

    /** 停止请求已登记但容器还在跑：显示「停止中」 */
    isStopping(row) {
      return row.state === 'running' && Boolean(row.stopped_by);
    },

    stateMeta(row) {
      if (this.isStopping(row)) return TRAIN_STOPPING;
      return TRAIN_STATES[row.state] || TRAIN_STATES.unknown;
    },

    pushMeta(state) {
      return ARCHIVE_STATES[state] || { label: state || '-', tag: 'info' };
    },

    /** 归档格的悬停提示：原因 + 时间 */
    pushTooltip(row) {
      const lines = [];
      if (row.push_message) lines.push(row.push_message);
      if (row.push_at) lines.push(`归档时间 ${this.formatTime(row.push_at)}`);
      return lines.join('；');
    },

    /** 0 与 false 都是有效值，只有 null / undefined / 空串按缺省显示 */
    dash(value) {
      return value === null || value === undefined || value === '' ? '-' : value;
    },

    formatTime(value) {
      return value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '-';
    },

    percentOf(row) {
      const { percent } = row.progress || {};
      return typeof percent === 'number' ? percent : 0;
    },

    /** 进度条按状态着色（训练机只给百分比） */
    progressStatus(row) {
      if (row.state === 'failed') return 'exception';
      if (row.state === 'finished') return 'success';
      return null;
    },

    isStale(row) {
      if (row.state !== 'running' || !row.last_modified) return false;
      return dayjs().diff(dayjs(row.last_modified), 'minute') >= STALE_MINUTES;
    },

    /** 「产物更新」显示相隔多久 */
    relativeTime(value) {
      if (!value) return '-';
      const mtime = dayjs(value);
      const minutes = dayjs().diff(mtime, 'minute');
      if (minutes < 1) return '刚刚';
      if (minutes < 60) return `${minutes} 分钟前`;
      const hours = dayjs().diff(mtime, 'hour');
      if (hours < 24) return `${hours} 小时前`;
      return `${dayjs().diff(mtime, 'day')} 天前`;
    },

    // ====== 操作 ======

    /** 把整行递过去先渲染；抽屉自己再拉最新 */
    handleDetail(row) {
      this.detailRun = row;
      this.detailVisible = true;
    },

    /** 抽屉拉到的详情回填列表，关掉后表格仍是新的 */
    handleRowUpdated(item) {
      const index = this.runs.findIndex(row => row.run_name === item.run_name);
      if (index >= 0) this.$set(this.runs, index, item);
      if (this.detailRun && this.detailRun.run_name === item.run_name) this.detailRun = item;
    },

    async handleStop(row) {
      // stop_mode=force（多卡）立刻结束；单卡跑完当前一轮再退
      const forceful = row.stop_mode === 'force';
      const detail = forceful
        ? '会立刻结束这次训练：多卡训练的当前这一轮不做收尾，权重与指标停在上一轮结束时'
          + '的状态。确定停止？'
        : '会让训练跑完当前这一轮再退出（约几分钟），之后不再有新的轮次，'
          + '权重按停止那一刻的状态保留。确定停止？';
      try {
        await this.$confirm(`停止「${row.run_name}」${detail}`, '停止训练', { type: 'warning' });
      } catch (err) {
        return;
      }
      this.stoppingName = row.run_name;
      try {
        // 发完信号即返回「已登记停止」的行，先填回表格
        const { item } = (await stopRun(row.run_name)).data;
        this.$message.success(forceful ? '已请求停止：立刻退出' : '已请求停止：本轮跑完即退出');
        if (item) this.handleRowUpdated(item);
        await this.loadRuns(true);
      } catch (err) {
        // 错误提示由响应拦截器统一弹出
      } finally {
        this.stoppingName = '';
      }
    },

    async handlePush(row) {
      try {
        await this.$confirm(
          `归档「${row.run_name}」会把产物上传到 MinIO，已存在的对象会跳过，只补缺的，`
            + '传输期间请勿关闭页面。确定归档？',
          '归档产物',
          { type: 'warning' },
        );
      } catch (err) {
        return;
      }
      this.pushingName = row.run_name;
      try {
        // 不覆盖，重复归档只补缺的
        const { message } = (await pushRun(row.run_name, false)).data;
        this.$message.success(message || '已归档');
        await this.loadRuns(true);
      } catch (err) {
        // 失败原因由响应拦截器弹出
      } finally {
        this.pushingName = '';
      }
    },

    /** 登记为模型：不自动注册，把任务名带进导入框，参数预填后由人确认 */
    handleRegister(row) {
      this.importRunName = row.run_name;
      this.importVisible = true;
    },

    /** 登记成功后重拉一次列表，行上就带上绑定的模型 */
    async handleModelImported() {
      await this.loadRuns(true);
    },

    async handleDelete(row) {
      try {
        await this.$confirm(
          `删除「${row.run_name}」会把它从任务列表里抹掉，并删掉训练机上的 run 目录与落盘日志，`
            + '不可恢复。已归档到 MinIO 的产物不受影响。确定删除？',
          '删除任务',
          { type: 'warning', confirmButtonText: '删除', confirmButtonClass: 'el-button--danger' },
        );
      } catch (err) {
        return;
      }
      this.deletingName = row.run_name;
      try {
        await deleteRun(row.run_name);
        this.$message.success('已删除');
        await this.loadRuns(true);
      } catch (err) {
        // 拒绝原因由响应拦截器弹出
      } finally {
        this.deletingName = '';
      }
    },

    // ====== 批量删除 ======

    handleSelectionChange(rows) {
      this.selected = rows || [];
    },

    /** 在跑的不给勾：与「停止」互斥，判据同行内删除按钮 */
    canDelete(row) {
      return row.state !== 'running';
    },

    /** 批量删除：逐个调接口，失败的点名带出 */
    async handleBatchDelete() {
      const rows = [...this.selected];
      if (!rows.length) return;
      try {
        await this.$confirm(
          `将删除选中的 ${rows.length} 个任务：从任务列表抹掉，并删掉训练机上的 run 目录与`
            + '落盘日志，不可恢复。已归档到 MinIO 的产物不受影响。确定删除？',
          '删除任务',
          { type: 'warning', confirmButtonText: '删除', confirmButtonClass: 'el-button--danger' },
        );
      } catch (err) {
        return;
      }
      this.batchDeleting = true;
      const failed = [];
      for (const row of rows) {
        try {
          await deleteRun(row.run_name);
        } catch (err) {
          failed.push(row.run_name);
        }
      }
      this.batchDeleting = false;
      const done = rows.length - failed.length;
      if (failed.length) {
        this.$message.warning(`已删除 ${done} 个；${failed.length} 个没删掉：${failed.join('、')}`);
      } else {
        this.$message.success(`已删除 ${done} 个`);
      }
      await this.loadRuns(true);
    },
  },
};
</script>

<style scoped>
.table-card {
  margin-bottom: 16px;
}

.table-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.table-header-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.keyword-input {
  width: 240px;
}

.state-select {
  width: 130px;
}

.total-count {
  color: #909399;
  font-size: 13px;
}

.mono {
  font-family: Consolas, Monaco, monospace;
  color: #606266;
}

.cell-sub {
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
}

/* 训练中且产物久未更新：时间标黄 */
.stale {
  color: #e6a23c;
}

.muted {
  color: #c0c4cc;
}

.danger-text {
  color: #f56c6c;
}
</style>
